/**
 * The founding form (M6 plan P33, spec §7.1), the Registration tab at the Charter Hub before founding: the fee, the
 * name, the ticker, the logo (editor) and the livery. The rules are `corpModel.ts foundingModel`; the worker judges.
 */
import type { ReactNode } from 'react'
import type { LiveryColors, LogoRecipe } from '../../strategic/types'
import { FocusButton } from '../focus'
import { keepTyping } from './typing'
import type { FoundingModel } from './corpModel'
import { LiveryPicker, liveryPickerSize } from './LiveryPicker'
import { LogoEditor, logoEditorSize } from './LogoEditor'

export interface FoundingProps {
  model: FoundingModel
  name: string
  ticker: string
  logo: LogoRecipe
  livery: LiveryColors
  busy: boolean
  status: string | null
  onName(name: string): void
  onTicker(ticker: string): void
  onLogo(logo: LogoRecipe): void
  onLivery(livery: LiveryColors): void
  onFound(): void
}

export function Founding(p: FoundingProps): ReactNode {
  const liveryFirst = logoEditorSize()
  return (
    <div className="sui fp-panel corp-panel" data-testid="corp-founding">
      <h3 className="corp-title">Register a corporation</h3>
      <p className="fp-meta">
        Registration fee{' '}
        <span className="ui-num" data-testid="corp-fee">
          {p.model.feeLabel}
        </span>
        , paid from your wallet. The corporation gets its own company account, a seat on the Charter and standings with every faction.
      </p>
      <div className="corp-fields">
        <label className="corp-field">
          Name
          <input
            type="text"
            maxLength={40}
            value={p.name}
            disabled={p.busy}
            data-testid="corp-name"
            onKeyDown={keepTyping}
            onChange={(e) => p.onName(e.target.value)}
          />
        </label>
        <label className="corp-field">
          Ticker
          <input
            type="text"
            maxLength={4}
            value={p.ticker}
            disabled={p.busy}
            data-testid="corp-ticker"
            onKeyDown={keepTyping}
            onChange={(e) => p.onTicker(e.target.value.toUpperCase())}
          />
        </label>
      </div>
      <h4 className="corp-sub">Logo</h4>
      <LogoEditor recipe={p.logo} busy={p.busy} onChange={p.onLogo} first={0} />
      <h4 className="corp-sub">Livery</h4>
      <LiveryPicker livery={p.livery} busy={p.busy} onChange={p.onLivery} first={liveryFirst} />
      {p.model.reason !== '' ? (
        <p className="fp-row__reason" data-testid="corp-found-reason">
          {p.model.reason}
        </p>
      ) : null}
      <FocusButton
        group="corp"
        index={liveryFirst + liveryPickerSize()}
        testId="corp-found"
        disabled={!p.model.canFound || p.busy}
        onActivate={p.onFound}
      >
        Register {p.model.ticker === '' ? 'the corporation' : p.model.ticker} for {p.model.feeLabel}
      </FocusButton>
      {p.status !== null ? (
        <p className="fp-status" role="status" data-testid="corp-status">
          {p.status}
        </p>
      ) : null}
    </div>
  )
}
